import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import StarChart from '../components/viz/StarChart';
import BipolarSlider from '../components/viz/BipolarSlider';
import HeroCodeReel from '../components/viz/HeroCodeReel';
import HairlineDivider from '../components/primitives/HairlineDivider';
import DisplayHeading from '../components/primitives/DisplayHeading';
import MonoCaption from '../components/primitives/MonoCaption';
import AXIS_DEFS from '../data/axisDefs';
import { getAllTypes } from '../data/typesData';

/* =====================================================
 * Layout containers
 * ===================================================== */

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${p => p.theme.colors.bgPrimary};
  color: ${p => p.theme.colors.text};
  overflow: hidden;

  /* 薄い星雲（CSSのみ） */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 40% at 30% 20%, rgba(156,123,201,0.08), transparent 60%),
      radial-gradient(ellipse 50% 35% at 75% 75%, rgba(107,140,203,0.07), transparent 65%),
      radial-gradient(ellipse 40% 30% at 50% 50%, rgba(208,138,138,0.04), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  max-width: ${p => p.$wide ? p.theme.containers.default : p.theme.containers.narrow};
  margin: 0 auto;
  padding: 96px 24px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: 64px 20px;
  }
  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 48px 16px;
  }
`;

const Section = styled.section`
  position: relative;
  z-index: 1;
  max-width: ${p => p.$wide ? p.theme.containers.default : p.theme.containers.narrow};
  margin: 0 auto;
  padding: 64px 24px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 48px 16px;
  }
`;

/* =====================================================
 * Hero
 * ===================================================== */

const Hero = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 12 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const HeroBrandMark = styled(motion.div)`
  font-family: ${p => p.theme.fonts.mono};
  font-size: clamp(1.05rem, 2vw, 1.3rem);
  font-weight: 600;
  letter-spacing: 0.36em;
  color: ${p => p.theme.colors.textMuted};
  text-align: center;
  margin: 0 0 20px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    letter-spacing: 0.28em;
  }
`;

const HeroBaseline = styled(motion.p)`
  margin: 28px auto 36px;
  /* PC幅では 1 行に収まるように max-width を広げる。
     モバイルでは画面幅で自動的に折り返す (keep-all で文節境界) */
  max-width: 56ch;
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-size: clamp(1.05rem, 1.8vw, 1.3rem);
  line-height: 1.95;
  color: ${p => p.theme.colors.textSecondary};
  text-align: center;
  word-break: keep-all;
  overflow-wrap: normal;
  line-break: strict;
`;

const CtaRow = styled(motion.div)`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
`;

const CtaPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 26px;
  background: ${p => p.theme.colors.gradGold};
  color: #1A1408;
  text-decoration: none;
  border-radius: ${p => p.theme.borderRadius.full};
  font-weight: 600;
  font-size: 0.92rem;
  letter-spacing: 0.04em;
  border: 1px solid rgba(230,199,126,0.55);
  box-shadow: ${p => p.theme.shadows.glowSoft};
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${p => p.theme.shadows.glow};
  }
`;

const CtaGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 26px;
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  text-decoration: none;
  border-radius: ${p => p.theme.borderRadius.full};
  font-weight: 500;
  font-size: 0.9rem;
  letter-spacing: 0.04em;
  border: 1px solid ${p => p.theme.colors.lineSoft};
  transition: border-color 0.25s ease, color 0.25s ease;

  &:hover {
    color: ${p => p.theme.colors.starGold};
    border-color: ${p => p.theme.colors.lineGold};
  }
`;

/* =====================================================
 * Five Axes
 * ===================================================== */

const AxisListCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 28px 32px;
  background: ${p => p.theme.colors.gradCard};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-radius: ${p => p.theme.borderRadius.md};

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 22px 20px;
  }
`;

const AxisListDivider = styled.div`
  height: 1px;
  background: ${p => p.theme.colors.lineFaint};
  margin: 6px 0;
`;

/* =====================================================
 * 24 Types Constellation Gallery
 * ===================================================== */

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;

  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TypeTile = styled(motion(Link))`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 14px 10px 14px;
  background: ${p => p.theme.colors.bgPrimary};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-radius: ${p => p.theme.borderRadius.sm};
  text-decoration: none;
  min-height: 180px;
  transition: border-color 0.25s ease, transform 0.25s ease, background 0.25s ease;

  &:hover {
    border-color: ${p => p.theme.colors.lineGold};
    transform: translateY(-2px);
    background: ${p => p.theme.colors.bgTertiary};
  }
`;

const TypeStarWrap = styled.div`
  width: 76px;
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const TypeCode = styled.span`
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  color: ${p => p.theme.colors.starGold};
  line-height: 1;
`;

const TypeName = styled.span`
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-size: 0.92rem;
  color: ${p => p.theme.colors.text};
  margin-top: 4px;
  letter-spacing: 0.01em;
  line-height: 1.2;
`;

const TypeArche = styled.span`
  font-family: ${p => p.theme.fonts.body};
  font-size: 0.7rem;
  color: ${p => p.theme.colors.textMuted};
  margin-top: 6px;
  text-align: center;
  line-height: 1.4;
`;

/* =====================================================
 * Philosophy section
 * ===================================================== */

const ProseBlock = styled.div`
  font-family: ${p => p.theme.fonts.body};
  font-size: 1.02rem;
  line-height: 1.95;
  color: ${p => p.theme.colors.textSecondary};

  p { margin: 0 0 1.3em; }
  p:last-child { margin-bottom: 0; }
  em {
    color: ${p => p.theme.colors.text};
    font-style: italic;
    font-family: ${p => p.theme.fonts.display};
  }
`;

const SectionEyebrow = styled(MonoCaption).attrs({ upper: true, gold: true })`
  display: block;
  margin-bottom: 12px;
`;

/* =====================================================
 * Component
 * ===================================================== */

// Hero ペンタゴン & 5軸セクションのデモスコア（実データではなく装飾用）
// 数値は 0-1 = 右極寄りの度合い (e.g. 0.77 → 自発 77%)
const DEMO_SCORES = {
  activation: 0.77,
  judgment:   0.79,
  choice:     0.89,
  resonance:  0.70,
  awareness:  0.98,
};

const HomePage = () => {
  const { t, lang } = useLanguage();
  const types = useMemo(() => getAllTypes(lang).slice(0, 24), [lang]);

  return (
    <Page>
      {/* ========== Hero ========== */}
      <Container>
        <Hero>
          <HeroBrandMark
            initial="hidden"
            animate="show"
            variants={fadeInUp}
          >
            STRUCT CODE
          </HeroBrandMark>

          <HeroCodeReel
            types={types}
            cycleMs={5500}
            reelMs={1300}
          />

          <CtaRow
            initial="hidden"
            animate="show"
            variants={fadeInUp}
            transition={{ delay: 0.12 }}
            style={{ marginTop: 24 }}
          >
            <CtaPrimary to="/current-diagnosis">
              {t('自分の天体コードを知る', 'Find your code')}
              <ArrowRight size={16} />
            </CtaPrimary>
            <CtaGhost to="/types">
              {t('タイプ一覧', 'All types')}
            </CtaGhost>
          </CtaRow>

          <HeroBaseline
            initial="hidden"
            animate="show"
            variants={fadeInUp}
            transition={{ delay: 0.22 }}
          >
            {t(
              '生まれた瞬間の星の位置と、25 の問いに浮かぶ「いまの自分」を、並べて読みます。',
              'We read together the sky at the moment you were born and the "you" today, drawn from 25 questions.'
            )}
          </HeroBaseline>
        </Hero>
      </Container>

      <HairlineDivider mark="✦" gold />

      {/* ========== STRUCT CODEとは ========== */}
      <Section>
        <SectionEyebrow>{t('STRUCT CODEとは', 'About STRUCT CODE')}</SectionEyebrow>
        <DisplayHeading
          as="h1"
          title={t(
            'STRUCT CODE は、内面を構造として読むためのシステム。',
            'STRUCT CODE is a system for reading the inner self as structure.'
          )}
          italic
          align="left"
          size="md"
        />
        <div style={{ height: 32 }} />
        <ProseBlock>
          <p>
            {t(
              '未来をあてるためのものではありません。自分のかたちを、読める形に翻訳するための道具です。',
              'It is not built to predict the future. It is a tool for translating the shape of yourself into something you can read.'
            )}
          </p>
          <p>
            {t(
              '西洋占星術と心理学を土台に、生まれた瞬間の天体配置と、25 の問いに浮かぶ「いまの自分」を、ひとつの構造として並べて読みます。',
              'Grounded in Western astrology and psychology, STRUCT CODE places the sky at the moment you were born alongside the "you" today, drawn from 25 questions, and reads the two as one structure.'
            )}
          </p>
        </ProseBlock>
      </Section>

      <HairlineDivider mark="☾" />

      {/* ========== Five Axes ========== */}
      <Section $wide id="five-axes">
        <DisplayHeading
          as="h2"
          title={t(
            '5 つの観測点から、自分を眺めてみる。',
            'Looking at yourself, from five quiet observation points.'
          )}
          subtitle={t(
            '5 つの軸は、それぞれ両極のあいだで揺れています。あなたのいまの位置を見てください。',
            'Each of the five axes sways between its two poles. Find where you stand right now.'
          )}
          align="center"
          italic
        />
        <div style={{ height: 40 }} />
        <AxisListCard
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
        >
          {AXIS_DEFS.map((axis, i) => (
            <React.Fragment key={axis.key}>
              {i > 0 && <AxisListDivider />}
              <BipolarSlider
                label={lang === 'en' ? axis.label_en : axis.label_ja}
                leftPole={lang === 'en' ? axis.pole_left_en : axis.pole_left_ja}
                rightPole={lang === 'en' ? axis.pole_right_en : axis.pole_right_ja}
                score={DEMO_SCORES[axis.key]}
                color={axis.color}
                showPercent
              />
            </React.Fragment>
          ))}
        </AxisListCard>
      </Section>

      <HairlineDivider mark="☾" />

      {/* ========== 24 Forms ========== */}
      <Section $wide>
        <DisplayHeading
          as="h2"
          title={t(
            '5 つの軸が交わる場所に、かたちが現れる。',
            'Where the five axes meet, a form appears.'
          )}
          subtitle={t(
            '24 のかたちは、読むための目印です。あなたの位置そのものは、軸の数値の側にあります。',
            'The twenty-four forms are landmarks for reading. Your actual position lives in the axis values.'
          )}
          align="center"
          italic
        />
        <div style={{ height: 40 }} />
        <TypeGrid>
          {types.map((type, i) => (
            <TypeTile
              key={type.code}
              to={`/types/${type.code}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{ duration: 0.4, delay: (i % 12) * 0.04 }}
            >
              <TypeStarWrap>
                <StarChart code={type.code} size={68} />
              </TypeStarWrap>
              <TypeCode>{type.code}</TypeCode>
              <TypeName>{type.name}</TypeName>
              {type.archetype && <TypeArche>{type.archetype}</TypeArche>}
            </TypeTile>
          ))}
        </TypeGrid>
      </Section>

      <HairlineDivider mark="── ✦ ──" gold />

      {/* ========== Final CTA ========== */}
      <Section>
        <Hero>
          <DisplayHeading
            as="h2"
            title={t(
              'あなたのコードは、あなたひとりのもの。',
              'Your code belongs to you alone.'
            )}
            italic
            align="center"
            size="md"
          />
          <div style={{ height: 28 }} />
          <CtaRow
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <CtaPrimary to="/current-diagnosis">
              {t('自分の天体コードを知る', 'Find your code')}
              <ArrowRight size={16} />
            </CtaPrimary>
          </CtaRow>
          <div style={{ height: 12 }} />
          <MonoCaption upper>
            {t(
              '診断は無料です。5-10分で診断が完了します。',
              'Free diagnosis · about 5-10 minutes.'
            )}
          </MonoCaption>
        </Hero>
      </Section>
    </Page>
  );
};

export default HomePage;
